import { canAccessPortalArea } from "@whitehaven/permissions";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { PortalSessionController } from "../content/usePortalSession";
import {
  createRoleAccessApiClient,
  RoleAccessApiError,
} from "./role-access-api";
import { RoleAccessEditor } from "./RoleAccessEditor";
import type {
  PortalCapabilityPermission,
  PortalRoleAccessSnapshot,
  Role,
} from "@whitehaven/permissions";

interface RoleAccessRouteProps {
  readonly session: PortalSessionController;
}

export const ADMIN_ROLE_ACCESS_PATH = "/admin/roles" as const;

export function RoleAccessRoute({ session }: RoleAccessRouteProps) {
  const client = useMemo(
    () => createRoleAccessApiClient(session.client.apiUrl),
    [session.client.apiUrl],
  );
  const [snapshot, setSnapshot] = useState<PortalRoleAccessSnapshot | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [error, setError] = useState<{
    readonly message: string;
    readonly correlationId: string | null;
  } | null>(null);

  const subject = session.data?.subject ?? null;
  const canView = Boolean(
    subject && canAccessPortalArea(subject, "admin-roles", "view"),
  );
  const canManage = Boolean(
    subject && canAccessPortalArea(subject, "admin-roles", "manage"),
  );
  const reload = useCallback(() => setLoadAttempt((value) => value + 1), []);

  useEffect(() => {
    if (session.status !== "authenticated" || !canView) {
      setSnapshot(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    void client
      .load(controller.signal)
      .then((nextSnapshot) => {
        if (controller.signal.aborted) return;
        setSnapshot(nextSnapshot);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError({
          message:
            caught instanceof Error
              ? caught.message
              : "Role access could not be loaded.",
          correlationId:
            caught instanceof RoleAccessApiError ? caught.correlationId : null,
        });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [canView, client, loadAttempt, session.status, session.data?.csrfToken]);

  const save = async (
    role: Role,
    permissions: readonly PortalCapabilityPermission[],
    expectedRevision: number,
  ) => {
    if (!session.data || !canManage) {
      throw new RoleAccessApiError(
        "Your role cannot change access grants.",
        403,
        ["AUTHORIZATION_DENIED"],
      );
    }

    try {
      const result = await client.replace(
        role,
        session.data.csrfToken,
        permissions,
        expectedRevision,
      );
      setSnapshot((current) =>
        current
          ? {
              ...current,
              roles: current.roles.map((candidate) =>
                candidate.key === result.role.key ? result.role : candidate,
              ),
            }
          : current,
      );
      session.retry();
      return result;
    } catch (caught: unknown) {
      if (caught instanceof RoleAccessApiError && caught.isConflict) reload();
      throw caught;
    }
  };

  if (session.status === "loading") {
    return (
      <section className="role-access-route" aria-labelledby="portal-title">
        <h1 id="portal-title" data-route-focus tabIndex={-1}>
          Role access
        </h1>
        <p className="role-access-route__state" role="status">
          Checking your secure session…
        </p>
      </section>
    );
  }

  if (session.status !== "authenticated" || !subject) {
    return (
      <section className="role-access-route" aria-labelledby="portal-title">
        <h1 id="portal-title" data-route-focus tabIndex={-1}>
          Role access
        </h1>
        <div className="role-access-route__error" role="alert">
          <strong>Secure sign-in required</strong>
          <p>{session.message ?? "Sign in to review role access."}</p>
          {session.correlationId && (
            <small>Reference: {session.correlationId}</small>
          )}
        </div>
      </section>
    );
  }

  if (!canView) {
    return (
      <section className="role-access-route" aria-labelledby="portal-title">
        <h1 id="portal-title" data-route-focus tabIndex={-1}>
          Role access
        </h1>
        <div className="role-access-route__error" role="alert">
          <strong>Access not granted</strong>
          <p>Your role does not have permission to view role access.</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="role-access-route" aria-labelledby="portal-title">
        <h1 id="portal-title" data-route-focus tabIndex={-1}>
          Role access
        </h1>
        <div className="role-access-route__error" role="alert">
          <strong>Role access is unavailable</strong>
          <p>{error.message}</p>
          {error.correlationId && (
            <small>Reference: {error.correlationId}</small>
          )}
          <button type="button" onClick={reload}>
            Try again
          </button>
        </div>
      </section>
    );
  }

  if (loading || !snapshot) {
    return (
      <section className="role-access-route" aria-labelledby="portal-title">
        <h1 id="portal-title" data-route-focus tabIndex={-1}>
          Role access
        </h1>
        <p className="role-access-route__state" role="status">
          Loading the capability matrix…
        </p>
      </section>
    );
  }

  return (
    <section className="role-access-route" aria-labelledby="portal-title">
      <RoleAccessEditor
        snapshot={snapshot}
        canManage={canManage}
        onSave={save}
        onReload={reload}
      />
    </section>
  );
}
